import { TimeSeriesCollectionData, VideoInfo, MonitorTargetType, MonitorSeverity, LogicalOperator, ComparisonOperator, MonitorActionType,
    MonitorCondition, IssueConditionValue, MonitorConditionThresholdType, RobotDefinition, RCNumericMetricType, RCNumericMetricDataPoint,
    MissingDataTreatment, TimeSeriesCollectionRawData, FileRequestTarget, FileRequestStatus, FileRequestAction,
    GatewayConfigData, GatewayDriver, GatewayConfig, isGatewayConfig } from '@moderepo/mode-apis';
import moment from 'moment';
import { TFunction } from 'i18next';
import { capitalizeAll, capitalizeFirst, getRobotNumericMetricInfoObject, MemorySize, XYChartProps } from '@moderepo/mode-app-components';
import { getGatewayConfigKVStoreKey } from '@moderepo/mode-app-components-internal';
import { fetchDeviceKVStore, selectDeviceKVStore, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { useContext, useEffect, useMemo } from 'react';
import { Agent, NumericMetricInfo, Project } from '@moderepo/mode-internal-apis';
import { PercentChartStyleOptions } from '../../components/robot-cloud/PercentMetricValueDisplay';
import { GaugeChartStyleOptions } from '../../components/robot-cloud/GaugeMetricValueDisplay';
import { getDateTimeDisplay } from '../containerUtils';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';



/**
 * This is where we define all CONSTANTS and UTIL functions used only by Robot Cloud projects. We don't want to add these
 * to the global utils or constants files because if we ever need to stop supporting robot cloud or remove robot cloud code,
 * we can do it easily just buy deleting this file.
 */


/**
 * We will use device's KV Store to store Robot Definition for each robot. These KV Store are used internally by the App Proxy and they are not meant
 * for the Agent to mess with so we will give them a special name which starts with 'MODE_'. All KV Stores that has prefix 'MODE_' will be hidden
 * from the agent.
 */
export const ROBOT_DEFINITION_KV_STORE_NAME: string = 'MODE_robotDefinition';


// The name of the field in Gateway Config where the file transfer config is stored
export const GATEWAY_FILE_TRANSFER_CONFIG_ATTR_NAME: string = 'FileTransferrer';


export const MONITOR_NAME_LENGTH_MIN: number = 1;
export const MONITOR_NAME_LENGTH_MAX: number = 50;
export const MONITOR_CONDITION_THRESHOLD_COUNT_MIN: number = 1;
export const MONITOR_CONDITION_THRESHOLD_COUNT_MAX: number = 10;
export const MONITOR_CONDITION_THRESHOLD_DURATION_MIN: number = 60;
export const MONITOR_CONDITION_THRESHOLD_DURATION_MAX: number = 3600;
export const MONITOR_CONDITION_SAMPLE_INTERVAL_MIN: number = 15;
export const MONITOR_CONDITION_SAMPLE_INTERVAL_MAX: number = 300;



// Set the upload file size a maximum to 700MB to prevent the user from selecting a vary large file
export const MAX_FILE_REQUEST_UPLOAD_FILE_SIZE = MemorySize.MEGABYTE * 700;



/**
 * Extension of the VideoInfo document from backend. We will add 'timestamp' and 'length' to the data because we need them to be able to
 * display the videos.
 */
export interface ExtVideoInfo extends VideoInfo {
    readonly timestamp: number;         // The video's timestamp
    readonly length: number;            // the video's length in MS
}



export enum MediaStatus {
    NONE = 'none',
    PAUSE = 'pause',
    PLAYING = 'playing',
    ENDED = 'ended',
}


/**
 * length - The total duration of the media in seconds, e.g. the video's length
 * current - The current time of the media in seconds, e.g. the current position of a video relative to the startTime
 * status - The current Play status of the media e.g. playing, paused, ended, etc...
 */
export interface MediaState {
    readonly length: number;
    readonly current: number;
    readonly status: MediaStatus;
}


export enum ChartValueType {
    POSITIVE_TO_NEGATIVE = 'positive_to_negative',      // Values are from positive to negative. smallest # = positive, largest # is negative
    NEGATIVE_TO_POSITIVE = 'negative_to_positive',      // Reverse of the POSITIVE_TO_NEGATIVE type
    NEUTRAL = 'neutral',                                // Values are neutral whether they are small number or large number
}



export const percentChartOptionsByMetricType: {[metricType: string]: PercentChartStyleOptions} = {
};


export const gaugeChartOptionsByMetricType: {[metricType: string]: GaugeChartStyleOptions} = {
    [RCNumericMetricType.SPEED]: {
        gaugeColor: '#00b8aa',
    },
    [RCNumericMetricType.ACCELERATION]: {
        handColor: '#990000',
    },
    [RCNumericMetricType.RMF]: {
        gaugeColor: '#DDAE7E',
    },
};



/**
 * Convert TimeSeriesCollectionData.data to RCMetricDataPoint array e.g.
 * From [
 *          ["2020-06-08T10:29:00-07:00", 0.641305780532686, 1, 1055, 37.558913011976514, -122.32208698802349, ...],
 *          ["2020-06-08T10:30:00-07:00", 0.640408530932434, 0, 1056, 37.55891172591692, -122.32208827408307, ...],
 *          ["2020-06-08T10:31:00-07:00", 0.6960005472580972, 1, 1057, 37.55891043085897, -122.32208956914101, ...],
 *          ["2020-06-08T10:32:00-07:00", 0.6645775924042752, 1, 1057, 37.558909126815436, -122.32209087318456, ...],
 *          ...
 *      ]
 * To   [
 *          {timestamp: "2020-06-08T10:29:00-07:00", timestampValue: 123456, values: {acceleration: 0.641305780532686, conn: 1, gps_altitude: 1055}},
 *          {timestamp: "2020-06-08T10:30:00-07:00", timestampValue: 123457, values: {acceleration: 0.640408530932434, conn: 0, gps_altitude: 1056}},
 *          {timestamp: "2020-06-08T10:31:00-07:00", timestampValue: 123458, values: {acceleration: 0.6960005472580972, conn: 1, gps_altitude: 1057}},
 *          {timestamp: "2020-06-08T10:32:00-07:00", timestampValue: 123459, values: {acceleration: 0.6645775924042752, conn: 1, gps_altitude: 1057}},
 *      ]
 *
 * @param collectionData
 * @param valueNames
 */
export const collectionDataToRobotMetricDataPoint = (
    collectionData: TimeSeriesCollectionData | TimeSeriesCollectionRawData, valueNames: readonly string[],
): RCNumericMetricDataPoint[] => {

    // collectionData.data is an array of measurement values, one for each metric.
    return collectionData.data.map((dataArray: (string | number | null)[]): RCNumericMetricDataPoint => {
        // The first element in data will be timestamp
        const timestamp = dataArray[0] as string;

        // convert the rest of other values from dataArray to a map of valueName => value
        // e.g.
        //    From: [34, 64, 47, 85.6]
        //    To:  {battery:34, speed: 64, network: 47, accelerator: 85.6}
        const metricValues = (dataArray.slice(1)).reduce(
            (result: {}, value: string | number | null, index: number): {} => {
                if (typeof value === 'string') {
                    return result;
                }
                return {
                    ...result,
                    [valueNames[index]]: value,
                };
            }, {},
        );

        return {
            timestamp,
            timestampValue: moment(timestamp).valueOf(),
            values        : metricValues,
        };
    });
};


/**
 * Create a yAxesRange of all the metrics and pass it to the XYCharts. This block of code will create this object
 * yAxesRange = {
 *      temperature: {min: 0, max: 100},
 *      pressure: {min: 0, max: 50},
 *      humidity: {min:-100, max:100}
 * }
 */
export const getNumericMetricsRange = (metrics: readonly NumericMetricInfo[]) => {
    const yAxesRange = metrics.reduce((
        result: XYChartProps['yAxesRange'], metric,
    ): XYChartProps['yAxesRange'] => {
        // metric.range is optional and it is an Object of 2 values e.g. {min, max} so don't need to create a range
        // for the metric if it does not have a "range" attribute or if it does not have any value
        if (metric.range && (metric.range.min !== undefined || metric.range.max !== undefined)) {
            return {
                ...result,
                [metric.name]: metric.range,
            };
        }
        return result;
    }, {});
    return yAxesRange;
};


/**
 * User friendly name for Alert target type.
 */
export const getMonitorTargetTypeName = (alertTargetType: MonitorTargetType): string => {
    const TypeName = Object.freeze({
        [MonitorTargetType.ROBOT_CLASS]: 'label.robot_class',
        [MonitorTargetType.ROBOT_ID]   : 'label.robot_id',
    });
    return TypeName[alertTargetType] || alertTargetType;
};



/**
 * User friendly name for Issue severity.
 */
export const getMonitorSeverityName = (issueSeverity: MonitorSeverity): string => {
    const TypeName = Object.freeze({
        [MonitorSeverity.MINOR]   : 'label.monitor_severity.minor',
        [MonitorSeverity.WARN]    : 'label.monitor_severity.warning',
        [MonitorSeverity.CRITICAL]: 'label.monitor_severity.critical',
    });
    return TypeName[issueSeverity] || issueSeverity.toString();
};


/**
 * User friendly name for LogicalOperators
 * @param operator
 */
export const getLogicalOperatorName = (operator: LogicalOperator): string => {
    const TypeName = Object.freeze({
        [LogicalOperator.AND]: 'label.logical_operator.and',
        [LogicalOperator.OR] : 'label.logical_operator.or',
    });
    return TypeName[operator] || operator as string;
};



/**
 * User friendly name for ComparisonOperator
 * @param operator
 */
export const getComparisonOperatorName = (operator: ComparisonOperator): string => {
    const TypeName = Object.freeze({
        [ComparisonOperator.LESS_THAN]            : 'label.comparison_operator.lt',
        [ComparisonOperator.LESS_THAN_OR_EQUAL]   : 'label.comparison_operator.le',
        [ComparisonOperator.EQUAL]                : 'label.comparison_operator.eq',
        [ComparisonOperator.GREATER_THAN_OR_EQUAL]: 'label.comparison_operator.ge',
        [ComparisonOperator.GREATER_THAN]         : 'label.comparison_operator.gt',
    });
    return TypeName[operator] || operator as string;
};


/**
 * User friendly name for MonitorActionType
 * @param type
 */
export const getMonitorActionTypeName = (type: MonitorActionType): string => {
    const TypeName = Object.freeze({
        [MonitorActionType.CREATE_ISSUE] : 'label.monitor_action_type.create_issue',
        [MonitorActionType.SEND_EMAIL]   : 'label.monitor_action_type.send_email',
        [MonitorActionType.CREATE_TICKET]: 'label.monitor_action_type.create_ticket',
        [MonitorActionType.SLACK_MESSAGE]: 'label.monitor_action_type.slack_message',
    });
    return TypeName[type] || type as string;
};


/**
 * Get an icon for specified monitor action type
 * @param type
 */
export const getMonitorActionTypeIcon = (type: MonitorActionType): string => {
    const TypeName = Object.freeze({
        [MonitorActionType.CREATE_ISSUE] : 'report',
        [MonitorActionType.SEND_EMAIL]   : 'email',
        [MonitorActionType.CREATE_TICKET]: 'fas fa-ticket-alt',
        [MonitorActionType.SLACK_MESSAGE]: 'fab fa-slack',
    });
    return TypeName[type] || type as string;
};



/**
 * User friendly name for MonitorConditionThresholdType
 * @param type
 */
export const getConditionThresholdTypeName = (type: MonitorConditionThresholdType): string => {
    const TypeName = Object.freeze({
        [MonitorConditionThresholdType.CONTINUOUS_COUNT]: 'label.condition_threshold_type.continuous_count',
        [MonitorConditionThresholdType.DURATION_COUNT]  : 'label.condition_threshold_type.duration_count',
    });
    return TypeName[type] || type as string;
};


/**
 * User friendly name for MissingDataTreatment
 * @param type
 */
export const getMissingDataTreatmentTypeName = (type: MissingDataTreatment): string => {
    const TypeName = Object.freeze({
        [MissingDataTreatment.SATISFIED]  : 'label.treat_missing_data_as_type.satisfied',
        [MissingDataTreatment.UNSATISFIED]: 'label.treat_missing_data_as_type.unsatisfied',
        [MissingDataTreatment.IGNORED]    : 'label.treat_missing_data_as_type.ignored',
    });
    return TypeName[type] || type as string;
};



/**
 * Get user friendly display for an MonitorCondition
 * @param condition
 * @param robotClass
 * @param trans
 * @param isReason - Whether we want to show the condition as a reason that an alert happened or just show the condition just as a condition e.g.
 *      "Because (Battery level < 10%)"             // Show the condition as a reason an alert happened
 *      "If (Battery level < 10%)"                  // Show the condition as a normal condition
 */
export const getMonitorConditionAsString = (
    robotCatalog: readonly RobotDefinition[] | undefined,
    condition: MonitorCondition, robotClass: string | undefined, trans: TFunction, isReason: boolean,
): string => {
    if (condition) {
        const conjunctionWord = trans(isReason ? 'label.conjunction.because' : 'label.conjunction.if');

        // Get the data from the condition object
        const {
            left, right, operator, threshold,
        } = condition;

        const leftMetric = getRobotNumericMetricInfoObject(robotCatalog, left.measurement.valueName, robotClass);
        const leftMeasurementName = leftMetric?.displayName || left.measurement.valueName;
        const rightMetric = operator && right ? getRobotNumericMetricInfoObject(robotCatalog, right.measurement.valueName, robotClass) : undefined;
        const rightMeasurementName = rightMetric?.displayName || right?.measurement.valueName;

        const leftString = `${leftMeasurementName} ${trans(getComparisonOperatorName(left.operator))} ${left.value}${leftMetric?.unit || ''}`;
        const rightString = operator && right
            ? `${rightMeasurementName} ${trans(getComparisonOperatorName(right.operator))} ${right.value}${rightMetric?.unit || ''}`
            : '';

        const thresholdString = trans(`label.threshold_display.${threshold.type}`, threshold.parameters);

        // The data we will pass to the translation function for interpolation
        const transData = {
            conjunction: conjunctionWord,
            condition_1: leftString,
            condition_2: rightString,
            threshold  : thresholdString,
        };

        if (operator === LogicalOperator.OR) {
            return capitalizeFirst(trans('label.joined_condition_display_with_or', transData));
        }
        if (operator === LogicalOperator.AND) {
            return capitalizeFirst(trans('label.joined_condition_display_with_and', transData));
        }
        return capitalizeFirst(trans('label.condition_display', transData));
    }

    return capitalizeAll(trans('label.none'));
};



/**
 * Get a user friendly display for an IssueCondition
 * @param condition
 * @param robotClass
 * @param trans
 */
export const getIssueConditionAsString = (
    robotCatalog: readonly RobotDefinition[] | undefined,
    condition: IssueConditionValue, robotClass: string | undefined, trans: TFunction,
): string => {
    if (condition) {
        const numericMetric = getRobotNumericMetricInfoObject(robotCatalog, condition.valueName, robotClass);

        return trans(condition.value !== undefined ? 'label.issue_triggered_value_display' : 'label.issue_triggered_missing_value_display', {
            value_name: numericMetric.displayName || numericMetric.name,
            value     : condition.value,
            unit      : numericMetric.unit || '',
            date      : getDateTimeDisplay(condition.timestamp, trans),
        });
    }

    return capitalizeAll(trans('label.none'));
};


/**
 * Given a robotId and a timestamp, create a video search key using the robotId and the year, month, day, etc... values from the timestamp
 * @param robotId
 * @param timestamp
 */
export const getVideoSearchKey = (robotId: string, timestamp: number): string => {
    return `/${robotId}/${moment(timestamp).utc().format('YYYY/MM/DD/HH/mm/ss')}`;
};



/**
 * User friendly name for File Request Target type.
 */
export const getFileRequestTargetName = (target: FileRequestTarget): string => {
    const TypeName = Object.freeze({
        [FileRequestTarget.CLOUD]         : 'file_transfer.target.cloud',
        [FileRequestTarget.DEVICE]        : 'file_transfer.target.device',
        [FileRequestTarget.CONTROL_SERVER]: 'file_transfer.target.control_server',
        [FileRequestTarget.ROBOT]         : 'file_transfer.target.robot',
    });
    return TypeName[target] || target.toString();
};



/**
 * User friendly name for File Request Status type.
 */
export const getFileRequestStatusName = (status: FileRequestStatus): string => {
    const TypeName = Object.freeze({
        [FileRequestStatus.CANCELED]  : 'file_transfer.status.canceled',
        [FileRequestStatus.CANCELLING]: 'file_transfer.status.cancelling',
        [FileRequestStatus.FAILED]    : 'file_transfer.status.failed',
        [FileRequestStatus.INITIATED] : 'file_transfer.status.initiated',
        [FileRequestStatus.PENDING]   : 'file_transfer.status.pending',
        [FileRequestStatus.RECEIVING] : 'file_transfer.status.receiving',
        [FileRequestStatus.SUCCEEDED] : 'file_transfer.status.succeeded',
        [FileRequestStatus.TIME_OUT]  : 'file_transfer.status.time_out',
        [FileRequestStatus.WARNING]   : 'file_transfer.status.warning',
        [FileRequestStatus.NOT_FOUND] : 'file_transfer.status.not_found',
    });
    return TypeName[status] || status.toString();
};



/**
 * User friendly name for File Request action type.
 */
export const getFileRequestActionName = (target: FileRequestAction): string => {
    const TypeName = Object.freeze({
        [FileRequestAction.CANCEL]                      : 'file_transfer.action.cancel',
        [FileRequestAction.DOWNLOAD_FROM_DEVICE]        : 'file_transfer.action.download_from_device',
        [FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER]: 'file_transfer.action.download_from_control_server',
        [FileRequestAction.DOWNLOAD_FROM_ROBOT]         : 'file_transfer.action.download_from_robot',
        [FileRequestAction.UPLOAD_TO_DEVICE]            : 'file_transfer.action.upload_to_device',
        [FileRequestAction.UPLOAD_TO_CONTROL_SERVER]    : 'file_transfer.action.upload_to_control_server',
        [FileRequestAction.UPLOAD_TO_ROBOT]             : 'file_transfer.action.upload_to_robot',
    });
    return TypeName[target] || target.toString();
};



/**
 * This is an interface for RC Gateway config which extends the GatewayConfig. All RC gateway config will have these attributes.
 * For now, only RC gateway use these attributes so we will keep them in RCGatewayConfig. If any of these attributes become common
 * attributes that all Gateway config has then we will move them into GatewayConfig.
 */
export interface RCGatewayConfig extends GatewayConfig {
    readonly drivers?: readonly GatewayDriver[] | undefined;
    readonly parameterGroups: GatewayConfigData;
}


/**
 * Type guard to check if a config object is a RCGatewayConfig
 * @param obj
 */
export const isRCGatewayConfig = (obj: unknown): obj is RCGatewayConfig => {
    const config = obj as RCGatewayConfig;
    return config
        && (config.drivers === undefined || config.drivers instanceof Array)
        && (typeof config.parameterGroups === 'object');
};



/**
 * The structure of the File Transfer config in the Gateway config.
 */
export interface RCGatewayFileTransferConfig {
    readonly d2cDirectories?: string | undefined;
    readonly d2cRobotDirectories?: string | undefined;
    readonly d2cMaximumFileSize?: number | undefined;
    readonly c2dDirectories?: string | undefined;
    readonly c2dRobotDirectories?: string | undefined;
    readonly c2dMaximumFileSize?: number | undefined;
    readonly useCustomDriver?: boolean | undefined;
}


/**
 * Type guard to check if a config object is a RCGatewayFileTransferConfig
 * @param obj
 */
export const isRCGatewayFileTransferConfig = (obj: unknown): obj is RCGatewayFileTransferConfig => {
    const config = obj as RCGatewayFileTransferConfig;
    return config
        && (config.d2cDirectories === undefined || typeof config.d2cDirectories === 'string')
        && (config.d2cRobotDirectories === undefined || typeof config.d2cRobotDirectories === 'string')
        && (config.d2cMaximumFileSize === undefined || typeof config.d2cMaximumFileSize === 'number')
        && (config.c2dDirectories === undefined || typeof config.c2dDirectories === 'string')
        && (config.c2dRobotDirectories === undefined || typeof config.c2dRobotDirectories === 'string')
        && (config.c2dMaximumFileSize === undefined || typeof config.c2dMaximumFileSize === 'number')
        && (config.useCustomDriver === undefined || typeof config.useCustomDriver === 'boolean');
};


/**
 * Custom Hook to get the Gateway config. Gateway configs are stored using DDP. The key for the config is something like
 * GATEWAY_CONFIG_{GATEWAY_ID}.
 * Gateway config has no defined structure, all we know is that it will be a JSON object. The content of the config is based on
 * the gateway definition for that gateway. All the attributes and their values in a gateway config are different between gateways.
 * @param gatewayId
 * @returns
 */
export const useGetGatewayConfig = (gatewayId: number) => {
    const { state, dispatch } = useContext(userAppDataContext);

    const gatewayConfigKVStore = selectDeviceKVStore(state, gatewayId, getGatewayConfigKVStoreKey(gatewayId));
    const gatewayConfig = useMemo(() => {
        if (gatewayConfigKVStore && isGatewayConfig(gatewayConfigKVStore.value)) {
            return gatewayConfigKVStore.value;
        }
        return undefined;
    }, [gatewayConfigKVStore]);

    useEffect(() => {
        if (!gatewayConfigKVStore) {
            dispatch(fetchDeviceKVStore(gatewayId, getGatewayConfigKVStoreKey(gatewayId))).catch(() => {
                // ignore error because the dispatch function already take care of displaying the error
            });
        }
    }, [dispatch, gatewayConfigKVStore, gatewayId]);

    return gatewayConfig;
};



/**
 * Custom Hook to get the RC Gateway config.
 * was a GatewayConfig.
 * @param gatewayId
 * @returns
 */
export const useGetRCGatewayConfig = (gatewayId: number) => {
    // First use the custom hook to get the base gateway config. RCGateConfig is GatewayConfig but with additional attributes
    const gatewayConfig = useGetGatewayConfig(gatewayId);

    // After the config is loaded, check the config to see if it is a type of RCGatewayConfig. If it is, cast the gatewayConfig
    // to RCGatewayConfig and return that object
    const rcGatewayConfig = useMemo(() => {
        if (isRCGatewayConfig(gatewayConfig)) {
            return gatewayConfig as RCGatewayConfig;
        }
        return undefined;
    }, [gatewayConfig]);

    return rcGatewayConfig;
};



/**
 * Given a list of file types in GLOB pattern, return an array of {name, value} pair, 1 for each file type's name and value
 * E.g.
 *  Input: "Log Files=/var/app/logs,Debug Files=/var/debug/log,Syslog=/var/log/syslog*"
 *  Output: [
 *              {name: "Log Files", value: "/var/app/logs"},
 *              {name: "Debug Files", value: "/var/debug/log"},
 *              {name: "Syslog", value: "/var/log/syslog*"}
 *          ]
 * @param fileTypeList
 * @returns
 */
export const parseFileTypeList = (fileTypeList: string | undefined): readonly {
    readonly name: string;
    readonly value: string;
}[] => {
    if (fileTypeList) {
        // Split the fileTypeList by commas so the result will be
        // ["Log Files=/var/app/logs", "Debug Files=/var/debug/log", "Syslog=/var/log/syslog*"]
        const items = fileTypeList.split(',');

        // then for each item in the items, split them by = and return an {name, value}
        return items.map((item: string) => {
            const pair = item.trim().split('=');

            // Just incase an item is NOT "name=value" format which mean pair[1] can be undefined. If pair[1] is undefined,
            // we will make the value the same as name. For example: If item="Log Files" then splitting the item will just
            // return an array of 1 string ["Log File"]. For this case, we will return {name:"Log File", value:"Log File"}
            return {
                name : pair[0],
                value: pair[1] !== undefined ? pair[1] : pair[0],
            };
        });
    }

    return [];
};


/**
 * File Transfer config is stored in the GatewayConfig. NOTE: different project store them under different attribute name
 * so we need to try to find this FileTransfer config based on some project settings.
 */
export const getGatewayFileTransferConfig = (
    gatewayConfig: RCGatewayConfig | undefined,
    project: Project | undefined,
    loggedInAgent: Agent | undefined,
): RCGatewayFileTransferConfig | undefined => {
    // Because the FileTransfer settings can be under different attribute name in the GatewayConfig for different project therefore
    // we need to get this attribute name config from the Project settings. By default, we will look for the file transfer settings
    // from the "gatewayConfig.FileTransfer" but if a project uses a different attribute name e.g. "gatewayConfig.AGVFileTransfer"
    // this will be set in the projectUIConfig and we need to get the attribute name from the uiSettings
    const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
        project?.supportToolSettings.projectType,
    )?.getCustomUIConfig(loggedInAgent, project);

    // fileTransferAttrName will be something like 'AGVFileTransfer' or 'FileTransfer'
    const fileTransferAttrName = projectUIConfig?.enabledFeatures?.fileTransfer?.gatewayFileTransferConfigAttrName
        || GATEWAY_FILE_TRANSFER_CONFIG_ATTR_NAME;

    // Use the fileTransferAttrName to find the file transfer settings
    const fileTransferConfig = gatewayConfig?.parameterGroups?.[fileTransferAttrName];

    // Make sure the fileTransferSettings has the valid data before using it
    if (isRCGatewayFileTransferConfig(fileTransferConfig)) {
        return fileTransferConfig;
    }

    return undefined;
};



/**
 * Get the list of supported file types for the specified action type. Return an Array of {name, value} pairs of file type name and value.
 * The settings for the supported file type will be in the GatewayConfig. If the action is a
 */
export const getSupportedFileType = (
    gatewayConfig: RCGatewayConfig | undefined,
    action: FileRequestAction,
    project: Project | undefined,
    loggedInAgent: Agent | undefined,
): readonly {
    readonly name: string;
    readonly value: string;
}[] => {
    const fileTransferSettings = getGatewayFileTransferConfig(gatewayConfig, project, loggedInAgent);

    // Make sure the fileTransferSettings has the valid data before using it
    if (fileTransferSettings) {
        switch (action) {
            case FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER:
            case FileRequestAction.DOWNLOAD_FROM_DEVICE:
                return parseFileTypeList(fileTransferSettings.d2cDirectories);
            case FileRequestAction.DOWNLOAD_FROM_ROBOT:
                return parseFileTypeList(fileTransferSettings.d2cRobotDirectories);
            case FileRequestAction.UPLOAD_TO_CONTROL_SERVER:
            case FileRequestAction.UPLOAD_TO_DEVICE:
                return parseFileTypeList(fileTransferSettings.c2dDirectories);
            case FileRequestAction.UPLOAD_TO_ROBOT:
                return parseFileTypeList(fileTransferSettings.c2dRobotDirectories);
            default:
                return [];
        }
    }

    // If we don't have fileTransfer settings from the GatewayConfig then return []
    return [];
};
